import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Form, Input, Select, Button, DatePicker, message, Modal } from 'antd';
import { UploadOutlined, EyeOutlined, CloseOutlined, FilePdfOutlined } from '@ant-design/icons';
// eslint-disable-next-line import/no-extraneous-dependencies
import dayjs from 'dayjs';
import { Flex, Text } from 'atoms';
import { BulkUploadModal } from 'componentsv2/modals';
import DocumentViewerModal from 'componentsv2/common/modals/DocumentViewerModal';
import { createDriverV2 } from 'store/driverSliceV2';
import { searchVehiclesV2 } from 'store/vehicleSliceV2';
import { searchVendorV2 } from 'store/vendorSliceV2';
import { getSignedUrl, uploadFilesToS3 } from 'store/commonSlice';
import { bulkUploadDrivers } from 'store/bulkUploadSlice';
import CreateDriverForm from './components/CreateDriverForm';
import DriverDetailsTable from './components/DriverDetailsTable';
import {
	PageContainer,
	FormRow,
	FormItem,
	BrowseButton,
	SubmitButton,
	FileUploadContainer,
} from './CreateDriverPageV2.styles';
import {
	ALLOWED_FILE_TYPES,
	FILE_TYPE_ERROR_MSG,
	DL_NUMBER_REQUIRED_MSG,
	UPLOAD_FAILED_MSG,
	FILE_UPLOAD_ERROR_LOG,
	BULK_UPLOAD_MESSAGE_KEY,
	BULK_UPLOAD_PROCESS_MESSAGE_KEY,
	TEXT_COLOR_DARK,
	FONT_SIZE_SMALL,
	BUTTON_TEXT_UPLOADING,
	BUTTON_TEXT_BROWSE,
	FILE_DISPLAY_CONTAINER_STYLE,
	FILE_ICON_STYLE,
	CLOSE_ICON_STYLE,
} from './CreateDriverPageV2.constants';
import { createDriverPayload, formatErrorMessage } from './CreateDriverPageV2.helpers';

const { Option } = Select;
const ALL_VEHICLE_TYPES_VALUE = '__ALL_VEHICLE_TYPES__';

export default function CreateDriverPageV2() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [form] = Form.useForm();
	const [driversList, setDriversList] = useState([]);
	const [vendorOptions, setVendorOptions] = useState([]);
	const [vehicleTypeOptions, setVehicleTypeOptions] = useState([]);
	const [drivingLicenseFile, setDrivingLicenseFile] = useState(null);
	const [drivingLicenseFileKey, setDrivingLicenseFileKey] = useState(null);
	const [uploadingLicense, setUploadingLicense] = useState(false);
	const [bulkUploadModalVisible, setBulkUploadModalVisible] = useState(false);
	const [bulkUploadLoading, setBulkUploadLoading] = useState(false);
	const [uploadedS3Key, setUploadedS3Key] = useState(null);
	const [uploadedEntity, setUploadedEntity] = useState(null);
	const [editModalVisible, setEditModalVisible] = useState(false);
	const [editingDriver, setEditingDriver] = useState(null);
	const [editForm] = Form.useForm();
	const [editDrivingLicenseFile, setEditDrivingLicenseFile] = useState(null);
	const [editDrivingLicenseFileKey, setEditDrivingLicenseFileKey] = useState(null);
	const fileInputRef = useRef(null);
	const editFileInputRef = useRef(null);

	// Document viewer modal state
	const [documentViewerVisible, setDocumentViewerVisible] = useState(false);
	const [viewingDocumentUrl, setViewingDocumentUrl] = useState('');
	const [viewingDocumentName, setViewingDocumentName] = useState('');
	const [viewingDocumentType, setViewingDocumentType] = useState('');

	// Fetch vendor options
	useEffect(() => {
		dispatch(
			searchVendorV2({
				filter_by: { is_active: true },
				pagination: { page: 1, per_page: 1000 },
			})
		).then((response) => {
			if (response?.rows) {
				const options = response.rows.map((vendor) => ({
					value: vendor.id,
					label: vendor.vendor_name || vendor.name || `Vendor ${vendor.id}`,
				}));
				setVendorOptions(options);
			}
		});
	}, [dispatch]);

	// Handle vendor change - fetch vehicle types based on selected vendor
	const handleVendorChange = async (vendorId) => {
		// Clear vehicle type when vendor changes
		form.setFieldsValue({ vehicle_type_ids: undefined });
		setVehicleTypeOptions([]);

		if (!vendorId) {
			return;
		}

		try {
			// Fetch vehicles by vendor_id to get vehicle types
			const response = await dispatch(
				searchVehiclesV2({
					filter_by: { vendor_id: vendorId, is_active: true },
					pagination: { page: 1, per_page: 1000 },
				})
			);

			const vehicles = response?.rows || [];

			// Extract unique vehicle types from vehicles
			const typeMap = new Map();
			vehicles.forEach((vehicle) => {
				const typeId =
					vehicle.vehicle_type_id ||
					vehicle.metadata?.vehicle_type_id ||
					vehicle.vehicle_type?.id;
				const typeName =
					vehicle.vehicle_type_name ||
					vehicle.metadata?.vehicle_type_name ||
					vehicle.vehicle_type?.vehicle_type_name ||
					vehicle.vehicle_type?.label;

				if (typeId && !typeMap.has(typeId)) {
					typeMap.set(typeId, {
						value: typeId,
						label: typeName || `Vehicle Type ${typeId}`,
					});
				}
			});

			const options = Array.from(typeMap.values());
			setVehicleTypeOptions(options);
		} catch (error) {
			console.error('Error fetching vehicle types:', error);
			setVehicleTypeOptions([]);
		}
	};

	// Handle vendor change in edit modal
	const handleEditVendorChange = async (vendorId, preserveVehicleTypeId = false) => {
		// Clear vehicle type when vendor changes (unless preserving for view mode)
		if (!preserveVehicleTypeId) {
			editForm.setFieldsValue({ vehicle_type_ids: undefined });
		}

		if (!vendorId) {
			setVehicleTypeOptions([]);
			return;
		}

		try {
			// Fetch vehicles by vendor_id to get vehicle types
			const response = await dispatch(
				searchVehiclesV2({
					filter_by: { vendor_id: vendorId, is_active: true },
					pagination: { page: 1, per_page: 1000 },
				})
			);

			const vehicles = response?.rows || [];

			// Extract unique vehicle types from vehicles
			const typeMap = new Map();
			vehicles.forEach((vehicle) => {
				const typeId =
					vehicle.vehicle_type_id ||
					vehicle.metadata?.vehicle_type_id ||
					vehicle.vehicle_type?.id;
				const typeName =
					vehicle.vehicle_type_name ||
					vehicle.metadata?.vehicle_type_name ||
					vehicle.vehicle_type?.vehicle_type_name ||
					vehicle.vehicle_type?.label;

				if (typeId && !typeMap.has(typeId)) {
					typeMap.set(typeId, {
						value: typeId,
						label: typeName || `Vehicle Type ${typeId}`,
					});
				}
			});

			const options = Array.from(typeMap.values());
			setVehicleTypeOptions(options);
		} catch (error) {
			console.error('Error fetching vehicle types:', error);
			setVehicleTypeOptions([]);
		}
	};

	const handleEditVehicleTypeChange = (selectedValues = []) => {
		if (!selectedValues.includes(ALL_VEHICLE_TYPES_VALUE)) {
			return;
		}

		editForm.setFieldsValue({
			vehicle_type_ids: vehicleTypeOptions.map((type) => type.value),
		});
	};

	// Handle view/edit driver
	const handleViewDriver = async (driver) => {
		setEditingDriver(driver);
		setEditDrivingLicenseFileKey(driver.driving_license_file);
		setEditDrivingLicenseFile(null);

		// Fetch vehicle types for the selected vendor first (preserve vehicle_type_ids)
		if (driver.vendor_id) {
			await handleEditVendorChange(driver.vendor_id, true);
		}

		// Set form values after vehicle types are loaded
		editForm.setFieldsValue({
			vendor_id: driver.vendor_id,
			vehicle_type_ids: driver.vehicle_type_ids,
			driver_name: driver.driver_name,
			mobile_number: driver.mobile_number,
			driving_license_number: driver.driving_license_number,
			driving_license_valid_till: driver.driving_license_valid_till
				? dayjs(driver.driving_license_valid_till)
				: null,
		});

		setEditModalVisible(true);
	};

	// Handle close edit modal
	const handleCloseEditModal = () => {
		setEditModalVisible(false);
		setEditingDriver(null);
		setEditDrivingLicenseFile(null);
		setEditDrivingLicenseFileKey(null);
		editForm.resetFields();
		if (editFileInputRef.current) {
			editFileInputRef.current.value = '';
		}
	};

	// Handle edit driver file change
	const handleEditFileChange = async (event) => {
		const file = event.target.files?.[0];
		if (!file) return;

		if (!ALLOWED_FILE_TYPES.includes(file.type)) {
			message.error(FILE_TYPE_ERROR_MSG);
			return;
		}

		setUploadingLicense(true);
		try {
			const signedUrlResponse = await dispatch(
				getSignedUrl({
					url_type: 'upload',
					data: {
						entity: 'driver',
						keys: [file.name],
					},
				})
			);

			if (signedUrlResponse?.data?.[0]) {
				const signedUrl = signedUrlResponse.data[0];
				await dispatch(
					uploadFilesToS3({
						url: signedUrl.url,
						data: file,
					})
				);
				setEditDrivingLicenseFileKey(signedUrl.filename);
				setEditDrivingLicenseFile(file);
				setUploadingLicense(false);
				message.success('Driving license uploaded successfully');
			}
		} catch (error) {
			console.error(FILE_UPLOAD_ERROR_LOG, error);
			setUploadingLicense(false);
			message.error(`${UPLOAD_FAILED_MSG} driving license`);
		}
	};

	// Handle remove driver from edit modal
	const handleRemoveDriverFromModal = () => {
		if (editingDriver) {
			setDriversList(driversList.filter((driver) => driver.id !== editingDriver.id));
			message.success('Driver removed from list');
			handleCloseEditModal();
		}
	};

	// Handle save changes in edit modal
	const handleSaveDriverChanges = async () => {
		try {
			const values = await editForm.validateFields([
				'vendor_id',
				'vehicle_type_ids',
				'driver_name',
				'mobile_number',
				'driving_license_number',
				'driving_license_valid_till',
			]);

			// Validate vendor is selected
			if (!values.vendor_id) {
				message.error('Please select a vendor');
				return;
			}

			// Validate vehicle type is selected
			if (!values.vehicle_type_ids || values.vehicle_type_ids.length === 0) {
				message.error('Please select a vehicle type');
				return;
			}

			const selectedVehicleTypeIds = (values.vehicle_type_ids || []).filter(
				(typeId) => typeId !== ALL_VEHICLE_TYPES_VALUE
			);

			// Validate vehicle type belongs to selected vendor
			const isValidVehicleType = selectedVehicleTypeIds.every((val) =>
				vehicleTypeOptions.some((option) => option.value === val)
			);
			if (!isValidVehicleType) {
				message.error(
					'Selected vehicle type is not associated with the selected vendor. Please select a valid vehicle type.'
				);
				editForm.setFields([
					{
						name: 'vehicle_type_ids',
						errors: [
							'Selected vehicle type is not associated with the selected vendor',
						],
					},
				]);
				return;
			}

			if (!editDrivingLicenseFileKey) {
				message.error('Please upload driving license document');
				return;
			}

			// Create clean driver object with only required fields
			const updatedDriver = {
				id: editingDriver.id, // Keep the temporary ID
				vendor_id: values.vendor_id,
				vendor_name: vendorOptions.find((v) => v.value === values.vendor_id)?.label || '-',
				vehicle_type_ids: selectedVehicleTypeIds,
				vehicle_type_name:
					selectedVehicleTypeIds
						.map((id) => vehicleTypeOptions.find((v) => v.value === id)?.label)
						.filter(Boolean)
						.join(', ') || '-',
				driver_name: values.driver_name,
				mobile_number: values.mobile_number,
				driving_license_file: editDrivingLicenseFileKey,
				driving_license_number: values.driving_license_number,
				driving_license_valid_till: values.driving_license_valid_till
					? dayjs(values.driving_license_valid_till).format('YYYY-MM-DD')
					: null,
			};

			setDriversList(
				driversList.map((driver) =>
					driver.id === editingDriver.id ? updatedDriver : driver
				)
			);
			message.success('Driver updated successfully');
			handleCloseEditModal();
		} catch (error) {
			console.error('Validation failed:', error);
		}
	};

	// Handle bulk upload
	const handleBulkUpload = () => {
		setBulkUploadModalVisible(true);
	};

	// Handle bulk upload modal close
	const handleBulkUploadClose = () => {
		setBulkUploadModalVisible(false);
		setUploadedS3Key(null);
		setUploadedEntity(null);
		setBulkUploadLoading(false);
	};

	// Handle file upload to S3 (called when file is selected in the modal)
	const handleBulkUploadFileToS3 = async (file) => {
		setBulkUploadLoading(true);
		message.loading({
			content: 'Uploading file to S3...',
			key: BULK_UPLOAD_MESSAGE_KEY,
		});

		try {
			// Step 1: Get signed URL for S3 upload
			const requestEntity = 'driver_bulk_upload_v2';
			const signedUrlRequest = {
				url_type: 'upload',
				data: {
					entity: requestEntity,
					keys: [file.name],
					content_type: 'text/csv',
				},
			};

			const signedUrlResponse = await dispatch(getSignedUrl(signedUrlRequest));

			if (!signedUrlResponse || signedUrlResponse.status !== 'success') {
				throw new Error('Failed to get S3 upload URL');
			}

			const uploadData = signedUrlResponse.data?.[0];
			if (!uploadData?.url || !uploadData?.key) {
				throw new Error('Invalid S3 upload URL response');
			}

			// Extract entity from the signed URL response
			// The entity should be in the request data that we sent
			// Use the entity from the request we sent to getSignedUrl
			const entity = signedUrlRequest.data?.entity || requestEntity;

			const { url: uploadUrl, key: s3Key } = uploadData;

			// Step 2: Upload file to S3
			const uploadResponse = await fetch(uploadUrl, {
				method: 'PUT',
				body: file,
				headers: {
					'Content-Type': file.type || 'text/csv',
				},
			});

			if (!uploadResponse.ok) {
				throw new Error(`S3 upload failed: ${uploadResponse.statusText}`);
			}

			// Store S3 key and entity for later submission
			// Always store the entity - it should be from the request we sent
			setUploadedS3Key(s3Key);
			setUploadedEntity(entity); // This should be 'driver_bulk_upload_v2'

			message.success({
				content: 'File uploaded successfully. Click Submit to process.',
				key: BULK_UPLOAD_MESSAGE_KEY,
				duration: 3,
			});
		} catch (error) {
			console.error('❌ [Bulk Upload] S3 upload failed:', error);
			message.error({
				content: `Failed to upload file: ${error.message}`,
				key: BULK_UPLOAD_MESSAGE_KEY,
				duration: 5,
			});
		} finally {
			setBulkUploadLoading(false);
		}
	};

	// Handle bulk upload submit (called when Submit button is clicked)
	const handleBulkUploadSubmit = async () => {
		if (!uploadedS3Key) {
			message.error('Please upload a file first');
			return;
		}

		setBulkUploadLoading(true);
		message.loading({
			content: 'Processing bulk upload...',
			key: BULK_UPLOAD_PROCESS_MESSAGE_KEY,
		});

		try {
			// Ensure entity is set - use stored entity or fallback
			const entityToUse = uploadedEntity || 'driver_bulk_upload_v2';

			// Step 3: Call bulk upload API with S3 key and entity
			const response = await dispatch(
				bulkUploadDrivers({
					s3_key: uploadedS3Key,
					entity: entityToUse,
					setErrors: (errors) => {
						console.error('Bulk upload errors:', errors);
					},
				})
			);

			if (response && response.status === 'success') {
				const { success_count, error_count, total_count, download_url } =
					response.data || {};

				message.success({
					content: `Bulk upload completed: ${success_count} succeeded${
						error_count > 0 ? `, ${error_count} failed` : ''
					} out of ${total_count} total`,
					key: BULK_UPLOAD_PROCESS_MESSAGE_KEY,
					duration: 5,
				});

				// If there are errors, show option to download error report
				if (error_count > 0 && download_url) {
					message.warning({
						content: (
							<span>
								Some drivers failed to upload.{' '}
								<a href={download_url} target="_blank" rel="noopener noreferrer">
									Download error report
								</a>
							</span>
						),
						duration: 10,
					});
				}

				// Close modal and refresh page
				handleBulkUploadClose();
				// Optionally refresh driver list or navigate
				message.info('Please refresh the page to see updated driver list');
			} else {
				throw new Error(response?.error?.message || 'Bulk upload failed');
			}
		} catch (error) {
			console.error('❌ [Bulk Upload] Processing failed:', error);
			message.error({
				content: `Failed to process bulk upload: ${error.message}`,
				key: BULK_UPLOAD_PROCESS_MESSAGE_KEY,
				duration: 5,
			});
		} finally {
			setBulkUploadLoading(false);
		}
	};

	// Handle viewing document
	const handleViewDocument = async (documentKey, documentName = 'Document') => {
		if (!documentKey) {
			message.error('No document key provided');
			return;
		}

		try {
			// Get signed URL for viewing (use same entity as upload)
			const response = await dispatch(
				getSignedUrl({
					url_type: 'download',
					data: {
						entity: 'driver',
						keys: [documentKey],
					},
				})
			);

			if (response && response.status === 'success' && response.data?.[0]?.url) {
				const documentUrl = response.data[0].url;

				// Determine document type from URL or key
				let docType = 'unknown';
				const lowerKey = documentKey.toLowerCase();
				if (lowerKey.includes('.pdf')) {
					docType = 'pdf';
				} else if (
					lowerKey.includes('.jpg') ||
					lowerKey.includes('.jpeg') ||
					lowerKey.includes('.png')
				) {
					docType = 'image';
				}

				setViewingDocumentUrl(documentUrl);
				setViewingDocumentName(documentName);
				setViewingDocumentType(docType);
				setDocumentViewerVisible(true);
			} else {
				console.error('❌ Failed to get signed URL:', response);
				message.error('Failed to load document - Invalid response');
			}
		} catch (error) {
			console.error('❌ Error loading document:', error);
			message.error(`Failed to load document: ${error.message}`);
		}
	};

	// Handle closing document viewer
	const handleCloseDocumentViewer = () => {
		setDocumentViewerVisible(false);
		setViewingDocumentUrl('');
		setViewingDocumentName('');
		setViewingDocumentType('');
	};

	// Helper: Submit single driver
	const submitSingleDriver = (driver, index) => {
		const payload = createDriverPayload(driver);

		return dispatch(
			createDriverV2({
				req_data: payload,
				setErrors: (errors) => {
					console.error('Validation errors for driver', index + 1, ':', errors);
				},
			})
		).then((response) => {
			if (response && response.status === 'error') {
				const errorMessage = response.error?.message || 'Failed to create driver';
				throw new Error(formatErrorMessage(errorMessage, driver));
			}
			return response;
		});
	};

	// Helper: Show submission results
	const showSubmissionResults = (successful, failed) => {
		// Show individual error messages
		failed.forEach((result, idx) => {
			message.error({
				content: result.reason?.message || 'Failed to create driver',
				duration: 10,
				key: `error-${idx}`,
			});
		});

		// Show summary message
		if (successful.length > 0) {
			message.warning({
				content: `${successful.length} driver(s) created successfully, but ${failed.length} failed. Please fix the errors and try again.`,
				duration: 8,
			});
		} else {
			message.error({
				content: 'All drivers failed to create. Please check the errors above.',
				duration: 8,
			});
		}
	};

	// Handle final submit
	const handleSubmit = async () => {
		if (driversList.length === 0) {
			message.warning('Please add at least one driver');
			return;
		}

		try {
			const promises = driversList.map(submitSingleDriver);
			const results = await Promise.allSettled(promises);

			const successful = results.filter((r) => r.status === 'fulfilled');
			const failed = results.filter((r) => r.status === 'rejected');

			if (failed.length > 0) {
				console.error('Failed submissions:', failed);
				showSubmissionResults(successful, failed);
			} else {
				message.success(`${driversList.length} driver(s) created successfully`);
				navigate('/drivers');
			}
		} catch (error) {
			console.error('❌ Submit error:', error);
			message.error({
				content: error.message || 'Failed to create drivers',
				duration: 10,
			});
		}
	};

	return (
		<PageContainer>
			{/* Create Driver Section */}
			<CreateDriverForm
				form={form}
				vendorOptions={vendorOptions}
				vehicleTypeOptions={vehicleTypeOptions}
				onVendorChange={handleVendorChange}
				drivingLicenseFile={drivingLicenseFile}
				setDrivingLicenseFile={setDrivingLicenseFile}
				drivingLicenseFileKey={drivingLicenseFileKey}
				setDrivingLicenseFileKey={setDrivingLicenseFileKey}
				uploadingLicense={uploadingLicense}
				setUploadingLicense={setUploadingLicense}
				fileInputRef={fileInputRef}
				dispatch={dispatch}
				getSignedUrl={getSignedUrl}
				uploadFilesToS3={uploadFilesToS3}
				driversList={driversList}
				setDriversList={setDriversList}
				onBulkUpload={handleBulkUpload}
				onViewDocument={handleViewDocument}
			/>

			{/* Driver Details Section */}
			<DriverDetailsTable driversList={driversList} onViewDriver={handleViewDriver} />

			{/* Submit Button - Always visible at bottom right */}
			<Flex justifyContent="flex-end" mt={3}>
				<SubmitButton type="button" onClick={handleSubmit}>
					Submit
				</SubmitButton>
			</Flex>

			{/* Bulk Upload Modal */}
			<BulkUploadModal
				visible={bulkUploadModalVisible}
				onClose={handleBulkUploadClose}
				onSubmit={handleBulkUploadSubmit}
				onFileUpload={handleBulkUploadFileToS3}
				title="Bulk Upload Drivers"
				description="Upload a CSV file containing driver information. The file should include columns for vendor, vehicle type, driver name, mobile number, and driving license details."
				templateUrl="/driver_bulk_upload_template_v2.csv"
				templateText="Download the CSV template here:"
				acceptedFormats=".csv"
				maxSizeMB={10}
				loading={bulkUploadLoading}
			/>

			{/* Edit Driver Modal */}
			<Modal
				title={null}
				open={editModalVisible}
				onCancel={handleCloseEditModal}
				footer={null}
				width={900}
				closeIcon={<CloseOutlined style={{ fontSize: 18 }} />}
				centered
			>
				{editingDriver && (
					<>
						<Form form={editForm} layout="vertical">
							<FormRow>
								<FormItem>
									<Form.Item
										label="Select Vendor"
										name="vendor_id"
										rules={[
											{ required: true, message: 'Please select vendor' },
										]}
									>
										<Select
											placeholder="Select"
											size="large"
											showSearch
											onChange={handleEditVendorChange}
										>
											{vendorOptions.map((vendor) => (
												<Option key={vendor.value} value={vendor.value}>
													{vendor.label}
												</Option>
											))}
										</Select>
									</Form.Item>
								</FormItem>
								<FormItem>
									<Form.Item
										label="Driver Name"
										name="driver_name"
										rules={[
											{ required: true, message: 'Please enter driver name' },
										]}
									>
										<Input
											placeholder="Eg : Venkatesh......."
											size="large"
											maxLength={255}
										/>
									</Form.Item>
								</FormItem>
							</FormRow>

							<FormRow>
								<FormItem>
									<Form.Item
										label="Vehicle Type"
										name="vehicle_type_ids"
										rules={[
											{
												required: true,
												message: 'Please select vehicle type',
											},
											{
												validator: (_, value) => {
													if (!value || value.length === 0) {
														return Promise.resolve();
													}
													const filteredValue = value.filter(
														(val) => val !== ALL_VEHICLE_TYPES_VALUE
													);
													const isValid = filteredValue.every((val) =>
														vehicleTypeOptions.some(
															(option) => option.value === val
														)
													);
													if (!isValid) {
														return Promise.reject(
															new Error(
																'Selected vehicle type is not associated with the selected vendor'
															)
														);
													}
													return Promise.resolve();
												},
											},
										]}
										dependencies={['vendor_id']}
									>
										<Select
											mode="multiple"
											placeholder="Select"
											size="large"
											showSearch
											onChange={handleEditVehicleTypeChange}
										>
											<Option value={ALL_VEHICLE_TYPES_VALUE}>All</Option>
											{vehicleTypeOptions.map((type) => (
												<Option key={type.value} value={type.value}>
													{type.label}
												</Option>
											))}
										</Select>
									</Form.Item>
								</FormItem>
								<FormItem>
									<Form.Item
										label="Mobile Number"
										name="mobile_number"
										rules={[
											{
												required: true,
												message: 'Please enter mobile number',
											},
											{
												pattern: /^[0-9]{10}$/,
												message:
													'Please enter a valid 10-digit mobile number',
											},
										]}
									>
										<Input
											placeholder="Eg : 9876545678"
											size="large"
											maxLength={20}
										/>
									</Form.Item>
								</FormItem>
							</FormRow>

							<FormRow>
								<FormItem>
									<Form.Item
										label="Driving License Number"
										name="driving_license_number"
										rules={[
											{
												required: true,
												message: DL_NUMBER_REQUIRED_MSG,
											},
											{
												validator(_, value) {
													if (value && value !== value.trimStart()) {
														return Promise.reject(
															new Error(
																'Driving license number should not have leading spaces'
															)
														);
													}
													return Promise.resolve();
												},
											},
										]}
									>
										<Input placeholder="Eg : DL0208735" size="large" />
									</Form.Item>
								</FormItem>
								<FormItem>
									<Form.Item
										label="Driving License Upload"
										required
										rules={[
											{
												validator: () => {
													if (!editDrivingLicenseFileKey) {
														return Promise.reject(
															new Error(
																'Please upload driving license'
															)
														);
													}
													return Promise.resolve();
												},
											},
										]}
									>
										<FileUploadContainer>
											<input
												ref={editFileInputRef}
												type="file"
												accept=".pdf,.png,.jpeg,.jpg"
												onChange={handleEditFileChange}
												style={{ display: 'none' }}
											/>
											<BrowseButton
												type="button"
												icon={<UploadOutlined />}
												onClick={() => editFileInputRef.current?.click()}
												loading={uploadingLicense}
												disabled={uploadingLicense}
											>
												{uploadingLicense
													? BUTTON_TEXT_UPLOADING
													: BUTTON_TEXT_BROWSE}
											</BrowseButton>
											{editDrivingLicenseFile && (
												<Flex
													alignItems="center"
													justifyContent="space-between"
													mt={2}
													p={2}
													style={FILE_DISPLAY_CONTAINER_STYLE}
												>
													<Flex alignItems="center" gap={2}>
														<FilePdfOutlined style={FILE_ICON_STYLE} />
														<Text
															fontSize={FONT_SIZE_SMALL}
															color={TEXT_COLOR_DARK}
														>
															{editDrivingLicenseFile.name}
														</Text>
													</Flex>
													<CloseOutlined
														style={CLOSE_ICON_STYLE}
														onClick={() => {
															setEditDrivingLicenseFile(null);
															setEditDrivingLicenseFileKey(null);
															if (editFileInputRef.current) {
																editFileInputRef.current.value = '';
															}
														}}
													/>
												</Flex>
											)}
											{editDrivingLicenseFileKey &&
												!editDrivingLicenseFile && (
													<Flex
														alignItems="center"
														justifyContent="space-between"
														mt={2}
														p={2}
														style={FILE_DISPLAY_CONTAINER_STYLE}
													>
														<Flex
															alignItems="center"
															gap={2}
															style={{ cursor: 'pointer' }}
															onClick={() =>
																handleViewDocument(
																	editDrivingLicenseFileKey,
																	'Driving License'
																)
															}
														>
															<FilePdfOutlined
																style={FILE_ICON_STYLE}
															/>
															<Text
																fontSize={FONT_SIZE_SMALL}
																color={TEXT_COLOR_DARK}
															>
																Existing file uploaded
															</Text>
															<EyeOutlined
																style={{
																	fontSize: 16,
																	color: '#1890ff',
																	marginLeft: 8,
																}}
															/>
														</Flex>
														<CloseOutlined
															style={CLOSE_ICON_STYLE}
															onClick={() => {
																setEditDrivingLicenseFile(null);
																setEditDrivingLicenseFileKey(null);
																if (editFileInputRef.current) {
																	editFileInputRef.current.value =
																		'';
																}
															}}
														/>
													</Flex>
												)}
										</FileUploadContainer>
									</Form.Item>
								</FormItem>
							</FormRow>

							<FormRow>
								<FormItem>
									<Form.Item
										label="Valid Till"
										name="driving_license_valid_till"
										rules={[
											{
												required: true,
												message: 'Please select valid till date',
											},
										]}
									>
										<DatePicker
											placeholder="Choose Date"
											size="large"
											style={{ width: '100%' }}
											format="DD-MM-YYYY"
											disabledDate={(current) =>
												current && current < dayjs().startOf('day')
											}
										/>
									</Form.Item>
								</FormItem>
								<FormItem>{/* Empty column for alignment */}</FormItem>
							</FormRow>
						</Form>

						<Flex justifyContent="space-between" mt={4}>
							<Button
								type="default"
								danger
								onClick={handleRemoveDriverFromModal}
								style={{
									height: 40,
									padding: '0 24px',
									borderColor: '#ff4d4f',
									color: '#ff4d4f',
								}}
							>
								Remove Driver
							</Button>
							<Button
								type="primary"
								onClick={handleSaveDriverChanges}
								style={{
									height: 40,
									padding: '0 24px',
									backgroundColor: '#1e3a8a',
									borderColor: '#1e3a8a',
								}}
							>
								Save Changes
							</Button>
						</Flex>
					</>
				)}
			</Modal>

			{/* Document Viewer Modal */}
			<DocumentViewerModal
				visible={documentViewerVisible}
				onClose={handleCloseDocumentViewer}
				documentUrl={viewingDocumentUrl}
				documentName={viewingDocumentName}
				documentType={viewingDocumentType}
			/>
		</PageContainer>
	);
}
